/**
 * Minimal client error reporting.
 *
 * Off unless `VITE_ERROR_REPORT_URL` is set (then nothing leaves the device).
 * When on, it POSTs one small JSON document per distinct error:
 *   { message, stack, route, appVersion, userAgent, ts }
 * and nothing else: no cookies (`credentials: 'omit'`), no referrer, no user,
 * account, partner, game or prompt data. `message` and `stack` are scrubbed
 * of e-mail addresses, long numbers and URL query strings; `route` is the
 * path alone with numeric / uuid segments generalised. At most 5 reports are
 * sent per page load and an error whose message was already sent is dropped.
 */

export interface ErrorReport {
  message: string;
  stack: string;
  route: string;
  appVersion: string;
  userAgent: string;
  ts: string;
}

export const MAX_REPORTS_PER_PAGE_LOAD = 5;
export const MAX_MESSAGE_LENGTH = 300;
export const MAX_STACK_LENGTH = 2000;

/** Browser noise that says nothing about the app. */
const IGNORED_MESSAGES = [
  /^ResizeObserver loop/i,
  /^Script error\.?$/i,
  /^Non-Error promise rejection captured/i,
];

/** Removes what could identify a person from free text. */
export function scrubText(text: string): string {
  return text
    .replace(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g, '[email]')
    .replace(/(https?:\/\/[^\s?#)'"]+)[?#][^\s)'"]*/gi, '$1')
    .replace(/\b[A-Za-z0-9_-]{32,}\b/g, '[token]')
    .replace(/\d{6,}/g, '[n]');
}

/** `/game/42/x?code=ABC#h` -> `/game/:id/x`. Never returns a query or hash. */
export function sanitizeRoute(pathname: string): string {
  const path = (pathname.split(/[?#]/)[0] || '/').slice(0, 200);
  return path
    .split('/')
    .map(segment =>
      /^\d+$/.test(segment) ||
      /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(segment) ||
      /^[A-Za-z0-9_-]{20,}$/.test(segment)
        ? ':id'
        : segment
    )
    .join('/');
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message || error.name;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error)?.slice(0, MAX_MESSAGE_LENGTH) ?? String(error);
  } catch {
    return String(error);
  }
}

export interface ReportContext {
  /** Defaults to the current location's path. */
  pathname?: string;
  appVersion?: string;
  userAgent?: string;
  now?: number;
}

function currentAppVersion(): string {
  return typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev';
}

export function buildErrorReport(
  error: unknown,
  context: ReportContext = {}
): ErrorReport {
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  return {
    message: scrubText(messageOf(error)).slice(0, MAX_MESSAGE_LENGTH),
    stack: scrubText(stack).slice(0, MAX_STACK_LENGTH),
    route: sanitizeRoute(
      context.pathname ??
        (typeof window === 'undefined' ? '/' : window.location.pathname)
    ),
    appVersion: context.appVersion ?? currentAppVersion(),
    userAgent:
      context.userAgent ??
      (typeof navigator === 'undefined' ? '' : navigator.userAgent),
    ts: new Date(context.now ?? Date.now()).toISOString(),
  };
}

export type SendReport = (url: string, body: string) => void;

/** `fetch` with `keepalive` (no cookies, no referrer); `sendBeacon` as the fallback. */
export const sendReport: SendReport = (url, body) => {
  try {
    if (typeof fetch === 'function') {
      void fetch(url, {
        method: 'POST',
        body,
        keepalive: true,
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        // text/plain keeps it a "simple" request: no CORS preflight.
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      }).catch(() => undefined);
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
    }
  } catch {
    // Reporting must never throw.
  }
};

export interface ErrorReporter {
  /** `true` when a report was sent. */
  report: (error: unknown, context?: ReportContext) => boolean;
}

export function createErrorReporter(options: {
  url: string | undefined;
  send?: SendReport;
  max?: number;
}): ErrorReporter {
  const url = options.url?.trim() ?? '';
  const send = options.send ?? sendReport;
  const max = options.max ?? MAX_REPORTS_PER_PAGE_LOAD;
  const seen = new Set<string>();
  let sent = 0;

  return {
    report(error, context) {
      if (url === '' || sent >= max) return false;
      try {
        const report = buildErrorReport(error, context);
        if (
          report.message === '' ||
          IGNORED_MESSAGES.some(pattern => pattern.test(report.message)) ||
          seen.has(report.message)
        ) {
          return false;
        }
        seen.add(report.message);
        sent += 1;
        send(url, JSON.stringify(report));
        return true;
      } catch {
        return false;
      }
    },
  };
}

let singleton: ErrorReporter | null = null;

function getReporter(): ErrorReporter {
  singleton ??= createErrorReporter({
    url: import.meta.env.VITE_ERROR_REPORT_URL,
  });
  return singleton;
}

/** Test hook: forget the page-load counters. */
export function resetErrorReporter(): void {
  singleton = null;
}

/** Report an error (no-op unless `VITE_ERROR_REPORT_URL` is set). */
export function reportError(error: unknown): void {
  getReporter().report(error);
}

/** Wires `window` errors and unhandled promise rejections. Returns the cleanup. */
export function installGlobalErrorReporting(
  target: Window = window,
  report: (error: unknown) => void = reportError
): () => void {
  const onError = (event: ErrorEvent) => {
    report(event.error ?? event.message);
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    report(event.reason);
  };
  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
  };
}
